import React, {useEffect, useState} from 'react';
import {
    App,
    Button,
    DatePicker,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Select,
    Space,
    Table,
    Tag,
    Tooltip
} from 'antd';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    DeleteOutlined,
    EditOutlined,
    GiftOutlined,
    PlusOutlined,
    TeamOutlined
} from '@ant-design/icons';
import {
    CreateInvitationCodeRequest,
    InvitationCode,
    invitationCodeService,
    UpdateInvitationCodeRequest,
    UserDto
} from '../../services/invitationCodeService';
import {Role, roleService} from '../../services/roleService';
import dayjs from 'dayjs';

const {Option} = Select;
const {RangePicker} = DatePicker;

const InvitationCodeManagement: React.FC = () => {
    const {message: messageApi} = App.useApp();
    const [codes, setCodes] = useState<InvitationCode[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [usersModalVisible, setUsersModalVisible] = useState(false);
    const [editingCode, setEditingCode] = useState<InvitationCode | null>(null);
    const [selectedCodeUsers, setSelectedCodeUsers] = useState<UserDto[]>([]);
    const [selectedCode, setSelectedCode] = useState<InvitationCode | null>(null);
    const [form] = Form.useForm();
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [keyword, setKeyword] = useState('');
    const [roles, setRoles] = useState<Role[]>([]);
    const [rolesLoading, setRolesLoading] = useState(false);

    useEffect(() => {
        loadCodes();
    }, [pagination.current, pagination.pageSize]);

    useEffect(() => {
        loadRoles();
    }, []);

    const loadCodes = async () => {
        setLoading(true);
        try {
            const data = await invitationCodeService.getList(
                pagination.current - 1,
                pagination.pageSize,
                keyword || undefined
            );
            setCodes(data.content);
            setPagination(prev => ({
                ...prev,
                total: data.totalElements,
            }));
        } catch (error) {
            messageApi.error('获取邀请码列表失败');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        setPagination(prev => ({...prev, current: 1}));
        loadCodes();
    };

    const loadRoles = async () => {
        setRolesLoading(true);
        try {
            const data = await roleService.getAllRoles();
            setRoles(data);
        } catch {
            // 角色列表加载失败不影响主要功能
        } finally {
            setRolesLoading(false);
        }
    };

    const getUserRoleName = (roleCode: string | undefined) => {
        if (!roleCode) return undefined;
        const role = roles.find(r => r.roleCode === roleCode);
        return role?.roleName;
    };

    const handleTableChange = (pag: any) => {
        setPagination(prev => ({
            ...prev,
            current: pag.current,
            pageSize: pag.pageSize,
        }));
    };

    const handleCreate = () => {
        setEditingCode(null);
        form.resetFields();
        form.setFieldsValue({maxUses: 1});
        setModalVisible(true);
    };

    const handleEdit = (code: InvitationCode) => {
        setEditingCode(code);
        form.setFieldsValue({
            code: code.code,
            roleCode: code.roleCode,
            maxUses: code.maxUses,
            status: code.status,
            expireAt: code.expireAt ? dayjs(code.expireAt) : null,
            remark: code.remark,
        });
        setModalVisible(true);
    };

    const handleDelete = async (id: number) => {
        try {
            await invitationCodeService.delete(id);
            messageApi.success('删除成功');
            loadCodes();
        } catch (error: any) {
            messageApi.error(error.message || '删除失败');
        }
    };

    const handleViewUsers = async (code: InvitationCode) => {
        try {
            setSelectedCode(code);
            const users = await invitationCodeService.getUsers(code.id);
            setSelectedCodeUsers(users);
            setUsersModalVisible(true);
        } catch (error) {
            messageApi.error('获取关联用户失败');
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            if (editingCode) {
                const updateRequest: UpdateInvitationCodeRequest = {
                    maxUses: values.maxUses,
                    status: values.status,
                    expireAt: values.expireAt ? values.expireAt.toISOString() : null,
                    remark: values.remark,
                };
                await invitationCodeService.update(editingCode.id, updateRequest);
                messageApi.success('更新成功');
            } else {
                const createRequest: CreateInvitationCodeRequest = {
                    code: values.code,
                    roleCode: values.roleCode,
                    maxUses: values.maxUses,
                    expireAt: values.expireAt ? values.expireAt.toISOString() : null,
                    remark: values.remark,
                };
                await invitationCodeService.create(createRequest);
                messageApi.success('创建成功');
            }

            setModalVisible(false);
            loadCodes();
        } catch (error: any) {
            messageApi.error(error.message || '操作失败');
        }
    };

    const columns = [
        {
            title: '邀请码',
            dataIndex: 'code',
            key: 'code',
            render: (code: string) => (
                <Tag color="gold" style={{fontFamily: 'monospace', fontSize: 14}}>
                    {code}
                </Tag>
            ),
        },
        {
            title: '指定角色',
            dataIndex: 'roleCode',
            key: 'roleCode',
            render: (roleCode: string | undefined) => {
                if (!roleCode) return <span style={{color: 'var(--pt-text-secondary)'}}>-</span>;
                const roleName = getUserRoleName(roleCode);
                return <Tag color="purple">{roleName || roleCode}</Tag>;
            },
        },
        {
            title: '使用情况',
            key: 'usage',
            render: (_: any, record: InvitationCode) => (
                <span>
                    <span style={{color: 'var(--pt-bull)'}}>{record.usedCount}</span>
                    <span style={{color: 'var(--pt-text-secondary)'}}>/ {record.maxUses}</span>
                </span>
            ),
        },
        {
            title: '剩余次数',
            dataIndex: 'remainingUses',
            key: 'remainingUses',
            render: (remaining: number) => (
                <Tag color={remaining > 0 ? 'green' : 'red'}>
                    {remaining} 次
                </Tag>
            ),
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                status === 'ACTIVE' ? (
                    <Tag icon={<CheckCircleOutlined/>} color="success">启用</Tag>
                ) : (
                    <Tag icon={<CloseCircleOutlined/>} color="error">禁用</Tag>
                )
            ),
        },
        {
            title: '过期时间',
            dataIndex: 'expireAt',
            key: 'expireAt',
            render: (expireAt: string | null) => {
                if (!expireAt) return <span style={{color: 'var(--pt-text-secondary)'}}>永久有效</span>;
                const isExpired = dayjs(expireAt).isBefore(dayjs());
                return (
                    <span style={{color: isExpired ? 'var(--pt-bear)' : 'var(--pt-text-primary)'}}>
                        {dayjs(expireAt).format('YYYY-MM-DD HH:mm')}
                        {isExpired && <span style={{marginLeft: 8}}>(已过期)</span>}
                    </span>
                );
            },
        },
        {
            title: '可用状态',
            dataIndex: 'available',
            key: 'available',
            render: (available: boolean) => (
                available ? (
                    <Tag color="green">可用</Tag>
                ) : (
                    <Tag color="orange">不可用</Tag>
                )
            ),
        },
        {
            title: '备注',
            dataIndex: 'remark',
            key: 'remark',
            ellipsis: true,
            render: (remark: string) => remark || '-',
        },
        {
            title: '创建时间',
            dataIndex: 'createdTime',
            key: 'createdTime',
            render: (time: string) => dayjs(time).format('YYYY-MM-DD HH:mm'),
        },
        {
            title: '操作',
            key: 'action',
            width: 180,
            render: (_: any, record: InvitationCode) => (
                <Space size="small">
                    <Tooltip title="编辑">
                        <Button
                            type="text"
                            icon={<EditOutlined/>}
                            onClick={() => handleEdit(record)}
                        />
                    </Tooltip>
                    <Tooltip title="查看用户">
                        <Button
                            type="text"
                            icon={<TeamOutlined/>}
                            onClick={() => handleViewUsers(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="确定删除此邀请码吗？"
                        onConfirm={() => handleDelete(record.id)}
                        okText="确定"
                        cancelText="取消"
                    >
                        <Tooltip title="删除">
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined/>}
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const userColumns = [
        {
            title: '用户名',
            dataIndex: 'username',
            key: 'username',
        },
        {
            title: '邮箱',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={status === 'ACTIVE' ? 'green' : 'red'}>
                    {status === 'ACTIVE' ? '正常' : status}
                </Tag>
            ),
        },
        {
            title: '注册时间',
            dataIndex: 'createdTime',
            key: 'createdTime',
            render: (time: string) => dayjs(time).format('YYYY-MM-DD HH:mm'),
        },
    ];

    return (
        <div style={{padding: 24}}>
            <div style={{marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <div style={{display: 'flex', gap: 12}}>
                    <Input.Search
                        placeholder="搜索邀请码或备注"
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        onSearch={handleSearch}
                        style={{width: 250}}
                        allowClear
                    />
                </div>
                <Button
                    type="primary"
                    icon={<PlusOutlined/>}
                    onClick={handleCreate}
                >
                    创建邀请码
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={codes}
                rowKey="id"
                loading={loading}
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total) => `共 ${total} 条`,
                }}
                onChange={handleTableChange}
            />

            {/* 创建/编辑弹窗 */}
            <Modal
                title={
                    <span>
                        <GiftOutlined style={{marginRight: 8, color: 'var(--pt-primary)'}}/>
                        {editingCode ? '编辑邀请码' : '创建邀请码'}
                    </span>
                }
                open={modalVisible}
                onOk={handleSubmit}
                onCancel={() => setModalVisible(false)}
                okText="确定"
                cancelText="取消"
            >
                <Form
                    form={form}
                    layout="vertical"
                    style={{marginTop: 16}}
                >
                    <Form.Item
                        name="code"
                        label="邀请码"
                        rules={editingCode ? [] : [
                            {max: 20, message: '邀请码最长20个字符'},
                        ]}
                        extra={editingCode ? undefined : '留空将自动生成随机邀请码'}
                    >
                        <Input
                            placeholder="留空自动生成"
                            disabled={!!editingCode}
                            prefix={<GiftOutlined style={{color: 'var(--pt-text-secondary)'}}/>}
                        />
                    </Form.Item>

                    <Form.Item
                        name="maxUses"
                        label="最大使用次数"
                        rules={[{required: true, message: '请输入最大使用次数'}]}
                    >
                        <InputNumber
                            min={1}
                            max={10000}
                            style={{width: '100%'}}
                            placeholder="最大使用次数"
                        />
                    </Form.Item>

                    <Form.Item
                        name="roleCode"
                        label="注册角色"
                        extra={editingCode ? '创建时指定，不可修改' : '不指定则默认 USER 角色'}
                    >
                        <Select
                            allowClear
                            disabled={!!editingCode}
                            placeholder="选择注册角色（可选）"
                            loading={rolesLoading}
                        >
                            {roles.map(role => (
                                <Option key={role.roleCode} value={role.roleCode}>
                                    {role.roleName} ({role.roleCode})
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    {editingCode && (
                        <Form.Item
                            name="status"
                            label="状态"
                        >
                            <Select>
                                <Option value="ACTIVE">启用</Option>
                                <Option value="INACTIVE">禁用</Option>
                            </Select>
                        </Form.Item>
                    )}

                    <Form.Item
                        name="expireAt"
                        label="过期时间"
                        extra="留空表示永久有效"
                    >
                        <DatePicker
                            showTime
                            style={{width: '100%'}}
                            placeholder="选择过期时间"
                            format="YYYY-MM-DD HH:mm"
                        />
                    </Form.Item>

                    <Form.Item
                        name="remark"
                        label="备注"
                    >
                        <Input.TextArea
                            rows={3}
                            placeholder="备注说明（如：给某某渠道使用）"
                            maxLength={500}
                            showCount
                        />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 查看关联用户弹窗 */}
            <Modal
                title={
                    <span>
                        <TeamOutlined style={{marginRight: 8, color: 'var(--pt-primary)'}}/>
                        关联用户 - {selectedCode?.code}
                    </span>
                }
                open={usersModalVisible}
                onCancel={() => setUsersModalVisible(false)}
                footer={null}
                width={700}
            >
                <div style={{marginBottom: 16, marginTop: 8}}>
                    <span style={{color: 'var(--pt-text-secondary)'}}>
                        已使用 <span style={{color: 'var(--pt-bull)'}}>{selectedCodeUsers.length}</span> 次
                    </span>
                </div>
                <Table
                    columns={userColumns}
                    dataSource={selectedCodeUsers}
                    rowKey="userId"
                    pagination={false}
                    size="small"
                    locale={{emptyText: '暂无用户使用此邀请码'}}
                />
            </Modal>
        </div>
    );
};

export default InvitationCodeManagement;
